import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { redirectToLogin } from "@/lib/auth";
import { useAuth } from "@/contexts/AuthContext";
import { UserPlus, CheckCircle, XCircle, Loader2 } from "lucide-react";

interface InviteInfo {
  email: string;
  name: string | null;
  role: string;
}

export default function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const { user } = useAuth();
  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    validateInvite();
  }, [token]);

  async function validateInvite() {
    try {
      const data = await api.get<InviteInfo>(`/api/v1/auth/invite/${token}`);
      setInvite(data);
    } catch (err: any) {
      setError(err.message || "Invalid invite link");
    } finally {
      setLoading(false);
    }
  }

  async function handleAccept() {
    if (!token) return;
    // Redirect to IOPass login with invite token stored
    // The callback bridge page will include the invite token in the exchange request
    await redirectToLogin("/dashboard", token);
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full mx-4 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Validating invite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full mx-4 text-center">
          <XCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h1 className="text-xl font-bold text-foreground mb-2">Invalid Invite</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-primary to-pink-500 text-white rounded-lg hover:opacity-90 transition-opacity font-medium"
          >
            Back to Home
          </a>
        </div>
      </div>
    );
  }

  if (user) {
    // User is already logged in — they may have already claimed this invite
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-pink-50">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full mx-4 text-center">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-foreground mb-2">Already Signed In</h1>
          <p className="text-muted-foreground mb-2">
            You're signed in as <strong>{user.email}</strong>.
          </p>
          <p className="text-muted-foreground mb-6">
            If this invite is for a different account, please sign out first and try again.
          </p>
          <div className="flex gap-3 justify-center">
            <a
              href="/dashboard"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-primary to-pink-500 text-white rounded-lg hover:opacity-90 transition-opacity font-medium"
            >
              Go to Dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-pink-50">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full mx-4">
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center mx-auto mb-4">
            <UserPlus className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">You're Invited!</h1>
          <p className="text-muted-foreground mt-2">
            You've been invited to join card.buzz
          </p>
        </div>

        <div className="bg-muted rounded-xl p-4 mb-6 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Email</span>
            <span className="font-medium text-foreground">{invite?.email}</span>
          </div>
          {invite?.name && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Name</span>
              <span className="font-medium text-foreground">{invite.name}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Role</span>
            <span className={`font-semibold capitalize ${
              invite?.role === "admin" ? "text-red-600" :
              invite?.role === "secretary" ? "text-blue-600" :
              "text-green-600"
            }`}>{invite?.role}</span>
          </div>
        </div>

        <p className="text-sm text-muted-foreground mb-6 text-center">
          Click below to sign in with your IOPass account and claim your invitation.
          If you don't have an IOPass account, you'll be able to create one.
        </p>

        <button
          onClick={handleAccept}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-primary to-pink-500 text-white rounded-xl hover:opacity-90 transition-opacity font-semibold text-lg"
        >
          <UserPlus className="w-5 h-5" />
          Accept Invite & Sign In
        </button>
      </div>
    </div>
  );
}
